'use client';

import { useContext, useEffect, useState, type ForwardedRef } from 'react';
import { TooltipTriggerStateContext as RACTooltipTriggerStateContext } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCheckCircle, IconCopy } from '../../icons/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { Button } from '../button/button.js';
import { IconButton } from '../button/icon-button.js';
import type { ActionButtonProps, BaseButtonProps } from '../button/types.js';
import { Tooltip } from '../tooltip/tooltip.js';
import type { AriaLabelingProps, CommonProps, StringLikeChildren } from '../types.js';
import { useCopyToClipboard } from './use-copy.js';

export interface CopyButtonProps
  extends
    CommonProps,
    AriaLabelingProps,
    Pick<BaseButtonProps, 'size'>,
    Pick<ActionButtonProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {
  /** The text displayed on the button. */
  children?: StringLikeChildren;
  /**
   * Determines the visual appearance of the button.
   * @default 'secondary'
   */
  variant?: 'secondary' | 'tertiary';
  /** The content to be copied. */
  value: string;
  /** The description that appears in a tooltip when the user hovers or focuses the button. */
  description?: string;
  /** A handler that gets called when the user triggers the copying. */
  onCopy?: () => void;
}

function CopyButton({ onCopy, description, ...props }: CopyButtonProps, ref: ForwardedRef<HTMLButtonElement>) {
  const [copyStatus, showCopyFeedback, copyToClipboard] = useCopyToClipboard();
  const [showCopyFeedbackLabel, setShowCopyFeedbackLabel] = useState(showCopyFeedback);

  useEffect(() => {
    let labelChangeTimeout: ReturnType<typeof setTimeout> | undefined;

    if (showCopyFeedback) {
      setShowCopyFeedbackLabel(true);
    } else {
      // Wait until the tooltip has animated away before changing the label
      labelChangeTimeout = setTimeout(() => setShowCopyFeedbackLabel(false), 200);
    }

    return () => {
      if (labelChangeTimeout) {
        clearTimeout(labelChangeTimeout);
      }
    };
  }, [showCopyFeedback]);

  const { children, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!children && !ariaLabel && !ariaLabelledBy) {
      console.warn('CopyButton requires one of children / aria-label / aria-labelledby for accessibility');
    }
  }, [children, ariaLabel, ariaLabelledBy]);

  const onPress = () => {
    copyToClipboard(props.value);
    onCopy?.();
  };

  const messages = useLocalizedMessages('copyToClipboard');

  return (
    <Tooltip
      isDisabled={props.isDisabled || (!showCopyFeedback && !description)}
      shouldCloseOnPress={false}
      label={
        showCopyFeedbackLabel
          ? copyStatus?.success
            ? messages.format('success')
            : messages.format('failure')
          : (description ?? '')
      }
      placement="top"
    >
      <CopyButtonInner {...props} buttonRef={ref} onCopy={onPress} showCopyFeedback={showCopyFeedback} />
    </Tooltip>
  );
}

/**
 * Displays a button that allows users to copy a specific value to clipboard.
 *
 * See [copy button usage guidelines](https://ui.cimpress.io/components/copy-button/).
 */
const _CopyButton = withStyleProps(forwardRef(CopyButton), 'CopyButton');

export { _CopyButton as CopyButton };

function CopyButtonInner({
  UNSAFE_className,
  UNSAFE_style,
  children,
  value,
  onCopy,
  buttonRef,
  showCopyFeedback,
  ...props
}: Omit<CopyButtonProps, 'description'> & {
  buttonRef: ForwardedRef<HTMLButtonElement>;
  showCopyFeedback: boolean;
}) {
  const tooltipTriggerState = useContext(RACTooltipTriggerStateContext);

  const icon = showCopyFeedback ? <IconCheckCircle /> : <IconCopy />;

  useEffect(() => {
    if (showCopyFeedback) {
      tooltipTriggerState?.open(true);
    } else {
      tooltipTriggerState?.close(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCopyFeedback]);

  return children ? (
    <Button {...props} ref={buttonRef} type="button" iconStart={icon} tone="base" onPress={onCopy}>
      {children}
    </Button>
  ) : (
    <IconButton
      {...props}
      ref={buttonRef}
      type="button"
      icon={icon}
      tone="base"
      aria-label={props['aria-label'] ?? ''}
      onPress={onCopy}
    />
  );
}
